
import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SERVICES } from '../data';

export const ServiceDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  const service = useMemo(() => SERVICES.find(s => s.slug === slug), [slug]);

  if (!service) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 py-32 text-center">
        <h1 className="text-5xl font-black mb-10 text-brand-deep">Pakalpojums nav atrasts</h1>
        <Link to="/pakalpojumi" className="bg-brand-accent text-white px-12 py-5 rounded-full font-black text-xs uppercase tracking-widest">Atpakaļ uz pakalpojumiem</Link>
      </div>
    );
  }

  const handleInquiry = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setTimeout(() => {
      setIsFormSubmitting(false);
      setFormSuccess(true);
    }, 1500);
  };

  const steps = [
    { title: 'Konsultācija', desc: 'Sākotnējā saruna, lai saprastu jūsu vēlmes, budžetu un brauciena mērķi.' },
    { title: 'Plānošana', desc: 'Mūsu eksperti izstrādā detalizētu piedāvājumu ar viesnīcām un loģistiku.' },
    { title: 'Saskaņošana', desc: 'Veicam nepieciešamās izmaiņas, lai plāns būtu perfekts tieši jums.' },
    { title: 'Realizācija', desc: 'Mēs parūpējamies par visām rezervācijām un dokumentu sagatavošanu.' }
  ];

  return (
    <div className="bg-white pb-32">
      {/* Immersive Header */}
      <section className="relative h-[600px] md:h-[700px] flex items-center overflow-hidden">
        <img src={service.image} className="absolute inset-0 w-full h-full object-cover brightness-[0.3]" alt={service.title} />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-deep/80 via-transparent to-transparent"></div>
        <div className="max-w-[1400px] mx-auto px-4 relative z-10 w-full pt-20">
           <div className="inline-flex items-center gap-4 bg-brand-accent/20 backdrop-blur-md border border-brand-accent/30 text-brand-accent px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.3em] mb-8">
             <i className={service.icon}></i> LEC Premium Service
           </div>
           {/* Adjusted title size to text-5xl md:text-8xl to match Blog page */}
           <h1 className="text-5xl md:text-8xl font-black text-white tracking-tighter leading-tight md:leading-none mb-12 max-w-5xl">
             {service.title}
           </h1>
           
           {/* Detailed Hero Badges - Updated Structure for Reference Style */}
           <div className="flex flex-wrap items-center gap-10 md:gap-16">
             <div className="flex items-center gap-4 group">
               <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-accent rounded-full flex items-center justify-center text-white text-lg shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                 <i className="fas fa-clock"></i>
               </div>
               <div className="flex flex-col">
                 <span className="text-[11px] md:text-[13px] font-black text-white/90 uppercase tracking-widest leading-none">24h</span>
                 <span className="text-[11px] md:text-[13px] font-black text-white/90 uppercase tracking-widest leading-none mt-1.5">reakcija</span>
               </div>
             </div>

             <div className="flex items-center gap-4 group">
               <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-accent rounded-full flex items-center justify-center text-white text-lg shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                 <i className="fas fa-check-circle"></i>
               </div>
               <div className="flex flex-col">
                 <span className="text-[11px] md:text-[13px] font-black text-white/90 uppercase tracking-widest leading-none">Kvalitātes</span>
                 <span className="text-[11px] md:text-[13px] font-black text-white/90 uppercase tracking-widest leading-none mt-1.5">garantija</span>
               </div>
             </div>

             <div className="flex items-center gap-4 group">
               <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-accent rounded-full flex items-center justify-center text-white text-lg shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                 <i className="fas fa-user-tie"></i>
               </div>
               <div className="flex flex-col">
                 <span className="text-[11px] md:text-[13px] font-black text-white/90 uppercase tracking-widest leading-none">Personīgs</span>
                 <span className="text-[11px] md:text-[13px] font-black text-white/90 uppercase tracking-widest leading-none mt-1.5">asistents</span>
               </div>
             </div>
           </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-[1400px] mx-auto px-4 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 items-start">
          
          <div className="lg:col-span-8 space-y-24">
            <section className="relative">
              <div className="absolute -top-10 -left-10 text-[180px] font-black text-brand-surface opacity-50 select-none z-0 leading-none">01</div>
              <div className="relative z-10 space-y-12">
                <h2 className="text-5xl font-black text-brand-deep tracking-tighter">Kas padara šo <br/> pakalpojumu <span className="text-brand-accent">īpašu?</span></h2>
                <p className="text-3xl font-black text-brand-deep italic border-l-[12px] border-brand-accent pl-12 leading-relaxed">
                  {service.shortDescription}
                </p>
                <div className="prose prose-2xl max-w-none text-brand-deep/70 font-medium leading-relaxed">
                  <p>{service.fullDescription}</p>
                </div>
              </div>
            </section>

            {/* Workflow / Steps Section */}
            <section className="bg-brand-surface rounded-2xl p-16">
              <h3 className="text-sm font-black uppercase tracking-[0.4em] text-brand-accent mb-16">Mūsu darba process:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {steps.map((step, i) => (
                  <div key={i} className="bg-white p-10 rounded-xl shadow-sm hover:shadow-xl transition-all group">
                    <div className="text-5xl font-black text-brand-surface group-hover:text-brand-accent/10 transition-colors mb-6 leading-none">0{i+1}</div>
                    <h4 className="text-2xl font-black text-brand-deep mb-4 tracking-tight">{step.title}</h4>
                    <p className="text-brand-deep/50 font-bold leading-relaxed">{step.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Value Highlights - Updated to match the requested image style */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-12">
               <div className="bg-brand-deep p-12 md:p-16 rounded-2xl relative overflow-hidden flex flex-col justify-center min-h-[350px]">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-brand-accent/10 rounded-full blur-[80px]"></div>
                  
                  <div className="relative z-10">
                    <h4 className="inline-block bg-[#0047FF] text-white text-[10px] md:text-[11px] font-black uppercase tracking-[0.3em] px-3 py-1 mb-8">
                      Galvenais ieguvums:
                    </h4>
                    <div className="text-2xl md:text-3xl font-black tracking-tighter leading-[1.6]">
                      <span className="bg-[#0047FF] text-white px-3 py-1.5 decoration-clone box-decoration-clone leading-[1.9]">
                        {service.value}
                      </span>
                    </div>
                  </div>
                  
                  <i className="fas fa-quote-right absolute bottom-10 right-10 text-6xl text-white/5"></i>
               </div>
               
               <div className="border border-brand-surface p-16 rounded-2xl flex flex-col justify-center bg-brand-surface/30">
                  <p className="text-xl font-bold text-brand-deep/60 leading-relaxed">
                    Mēs ticam, ka katra detaļa ir svarīga. Mūsu komanda sastāv no nozares profesionāļiem ar gadu desmitu pieredzi tūrismā un loģistikā, nodrošinot nevainojamu izpildi.
                  </p>
               </div>
            </section>
          </div>

          {/* Sticky Inquiry Sidebar */}
          <aside className="lg:col-span-4 sticky top-32">
            <div className="bg-white rounded-2xl p-12 shadow-[0_30px_100px_rgba(0,0,0,0.1)] border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
              
              {!formSuccess ? (
                <>
                  <h3 className="text-4xl font-black mb-10 text-brand-deep tracking-tighter leading-tight">Pieteikt <span className="text-brand-accent">bezmaksas</span> konsultāciju</h3>
                  <form onSubmit={handleInquiry} className="space-y-8">
                    <div className="space-y-2">
                      <label className="block text-[11px] font-black uppercase text-brand-deep/30 px-4 tracking-widest">Pilnais vārds</label>
                      <input required className="w-full p-6 bg-brand-surface border-2 border-transparent focus:border-brand-accent rounded-2xl outline-none font-black text-brand-deep transition-all placeholder:text-brand-deep/20" placeholder="Jānis Bērziņš" />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-[11px] font-black uppercase text-brand-deep/30 px-4 tracking-widest">E-pasts</label>
                      <input required type="email" className="w-full p-6 bg-brand-surface border-2 border-transparent focus:border-brand-accent rounded-2xl outline-none font-black text-brand-deep transition-all placeholder:text-brand-deep/20" placeholder="janis@mail.lv" />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-[11px] font-black uppercase text-brand-deep/30 px-4 tracking-widest">Tālrunis</label>
                      <input required type="tel" className="w-full p-6 bg-brand-surface border-2 border-transparent focus:border-brand-accent rounded-2xl outline-none font-black text-brand-deep transition-all placeholder:text-brand-deep/20" placeholder="+371 200..." />
                    </div>
                    <button 
                      disabled={isFormSubmitting}
                      className="w-full bg-brand-accent text-white py-7 rounded-2xl font-black text-sm uppercase tracking-[0.3em] hover:bg-brand-accentHover transition-all shadow-2xl shadow-orange-100/50 active:scale-95"
                    >
                      {isFormSubmitting ? <i className="fas fa-circle-notch fa-spin"></i> : 'SĀKT PLĀNOŠANU'}
                    </button>
                    <p className="text-[10px] text-center font-bold text-brand-deep/30 uppercase tracking-widest leading-relaxed">
                      Mēs sazināsimies ar jums 24 stundu laikā, lai vienotos par ērtāko laiku.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-20 animate-fade-in">
                  <div className="w-24 h-24 bg-brand-surface text-brand-accent rounded-full flex items-center justify-center mx-auto mb-10 text-4xl shadow-inner">
                    <i className="fas fa-check"></i>
                  </div>
                  <h3 className="text-4xl font-black mb-6 text-brand-deep tracking-tighter">Paldies!</h3>
                  <p className="text-brand-deep/60 font-bold text-lg leading-relaxed mb-12">Jūsu pieteikums ir saņemts. Mūsu speciālists ar jums sazināsies pavisam drīz.</p>
                  <button onClick={() => setFormSuccess(false)} className="text-brand-accent font-black text-[11px] uppercase tracking-widest border-b-2 border-brand-accent pb-1 hover:text-brand-accentHover transition-colors">Sūtīt vēlreiz</button>
                </div>
              )}
            </div>

            {/* Related Link Card */}
            <div className="mt-12 bg-brand-surface p-10 rounded-2xl border border-gray-100 flex items-center gap-8 group cursor-pointer hover:bg-white transition-all">
               <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center text-brand-accent text-2xl shadow-sm group-hover:scale-110 transition-transform"><i className="fas fa-info-circle"></i></div>
               <div>
                  <h4 className="font-black text-brand-deep text-sm uppercase tracking-widest">Biežāk uzdotie jautājumi</h4>
                  <p className="text-[11px] font-black text-brand-deep/30 uppercase tracking-widest">Uzzini vairāk</p>
               </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};
